import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { MoreHorizontal, Trash2, Edit2, Reply } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { CommentWithAuthor, User, postsApi } from '@/lib/api'
import { formatRelativeTime, getInitials } from '@/lib/utils'
import { Textarea } from '@/components/ui/textarea'

interface CommentCardProps {
  comment: CommentWithAuthor
  postId: string
  currentUser?: User | null
  onReply?: (commentId: string, content: string) => void
  showReplies?: boolean
  level?: number
}

export function CommentCard({
  comment,
  postId,
  currentUser,
  onReply,
  showReplies = true,
  level = 0,
}: CommentCardProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [isReplying, setIsReplying] = useState(false)
  const [editContent, setEditContent] = useState(comment.comment.content)
  const [replyContent, setReplyContent] = useState('')
  const [showAllReplies, setShowAllReplies] = useState(false)
  const queryClient = useQueryClient()

  const isOwner = currentUser?.id === comment.comment.author_id
  const author = comment.author
  const displayName = author?.displayName || author?.display_name || 'Unknown User'
  const username = author?.username || 'unknown'
  const avatarUrl = author?.avatarUrl

  const deleteMutation = useMutation({
    mutationFn: () => postsApi.deleteComment(comment.comment.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', postId] })
      queryClient.invalidateQueries({ queryKey: ['feed'] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: (content: string) => postsApi.updateComment(comment.comment.id, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', postId] })
      setIsEditing(false)
    },
  })

  const replyMutation = useMutation({
    mutationFn: (content: string) =>
      postsApi.createReply(comment.comment.id, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', postId] })
      queryClient.invalidateQueries({ queryKey: ['replies', comment.comment.id] })
      setReplyContent('')
      setIsReplying(false)
      setShowAllReplies(true)
    },
  })

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this comment?')) {
      deleteMutation.mutate()
    }
  }

  const handleUpdate = () => {
    if (editContent.trim()) {
      updateMutation.mutate(editContent)
    }
  }

  const handleReply = () => {
    if (replyContent.trim()) {
      replyMutation.mutate(replyContent)
    }
  }

  const hasReplies = comment.comment.reply_count > 0
  const displayedReplies = showAllReplies
    ? comment.replies || []
    : (comment.replies || []).slice(0, 2)

  return (
    <div className={`space-y-3 ${level > 0 ? 'ml-12' : ''}`}>
      <div className="flex gap-3">
        <Avatar className="h-8 w-8 flex-shrink-0">
          <AvatarImage src={avatarUrl} />
          <AvatarFallback className="text-xs">{getInitials(displayName)}</AvatarFallback>
        </Avatar>

        <div className="flex-1 space-y-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium">{displayName}</p>
              <p className="text-xs text-muted-foreground">@{username}</p>
              <p className="text-xs text-muted-foreground">·</p>
              <p className="text-xs text-muted-foreground">
                {formatRelativeTime(comment.comment.created_at)}
              </p>
            </div>

            {isOwner && !isEditing && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7">
                    <MoreHorizontal className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setIsEditing(true)}>
                    <Edit2 className="mr-2 h-3 w-3" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleDelete}
                    className="text-destructive"
                  >
                    <Trash2 className="mr-2 h-3 w-3" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {isEditing ? (
            <div className="space-y-2">
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="min-h-[60px] text-sm"
                placeholder="Edit your comment..."
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleUpdate}
                  disabled={!editContent.trim() || updateMutation.isPending}
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditContent(comment.comment.content)
                    setIsEditing(false)
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm text-foreground whitespace-pre-wrap">
                {comment.comment.content}
              </p>

              {level === 0 && currentUser && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setIsReplying(!isReplying)}
                >
                  <Reply className="mr-1 h-3 w-3" />
                  Reply
                </Button>
              )}
            </>
          )}

          {isReplying && (
            <div className="space-y-2 pt-2">
              <Textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                className="min-h-[60px] text-sm"
                placeholder={`Reply to ${displayName}...`}
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleReply}
                  disabled={!replyContent.trim() || replyMutation.isPending}
                >
                  {replyMutation.isPending ? 'Replying...' : 'Reply'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setReplyContent('')
                    setIsReplying(false)
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Replies */}
      {showReplies && hasReplies && (
        <div className="space-y-3">
          {displayedReplies.map((reply) => (
            <CommentCard
              key={reply.comment.id}
              comment={reply}
              postId={postId}
              currentUser={currentUser}
              showReplies={false}
              level={level + 1}
            />
          ))}

          {comment.comment.reply_count > 2 && !showAllReplies && (
            <Button
              variant="ghost"
              size="sm"
              className="ml-12 h-6 text-xs text-muted-foreground"
              onClick={() => setShowAllReplies(true)}
            >
              View {comment.comment.reply_count - 2} more{' '}
              {comment.comment.reply_count - 2 === 1 ? 'reply' : 'replies'}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
